import { router } from "@inertiajs/react";
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from "lucide-react";
import { IconButton } from "@/components/IconButton";
import { Button } from "@/components/Button";
import { Menu } from "@/components/Menu";

/*
 * TablePager
 *
 * Server-driven page/per-page bar that lives at the bottom of a list view.
 * Page changes do an Inertia partial reload of one or more named props, so
 * other state on the host page (filters, sibling tabs, scroll position) is
 * preserved.
 *
 * URL param names are namespaced via `pageParam` / `perPageParam` so multiple
 * paginated lists on the same page don't collide (e.g. the per-source
 * Allocations tab uses "allocations_page" / "allocations_per_page" while the
 * standalone list uses plain "page" / "per_page").
 */
export interface TablePagination {
  page: number;
  perPage: number;
  total: number;
  totalPages: number;
  perPageOptions: number[];
  pageParam:    string;
  perPageParam: string;
}

interface TablePagerProps {
  pagination: TablePagination;
  /** Inertia partial-reload `only` props — defaults to a full reload. */
  reloadProps?: string[];
  /** Item label for the "Showing X–Y of N {plural}" line. */
  itemLabel?: { singular: string; plural: string };
}

export function TablePager({
  pagination,
  reloadProps,
  itemLabel = { singular: "row", plural: "rows" },
}: TablePagerProps) {
  const { page, perPage, total, totalPages, perPageOptions, pageParam, perPageParam } = pagination;
  const startRow = total === 0 ? 0 : (page - 1) * perPage + 1;
  const endRow   = Math.min(page * perPage, total);

  const reload = (nextPage: number, nextPerPage: number) => {
    const url = new URL(window.location.href);
    const params = url.searchParams;
    if (nextPage > 1) params.set(pageParam, String(nextPage)); else params.delete(pageParam);
    if (nextPerPage !== perPageOptions[0]) params.set(perPageParam, String(nextPerPage));
    else params.delete(perPageParam);

    router.get(
      url.pathname + (params.toString() ? `?${params}` : ""),
      {},
      {
        only: reloadProps,
        preserveState: true,
        preserveScroll: true,
        replace: true,
      },
    );
  };

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border-subtle px-4 py-2 text-xs text-fg-subtle tabular">
      <div className="flex items-center gap-2">
        <span>Rows per page</span>
        <Menu.Root>
          <Menu.Trigger asChild>
            <Button intent="ghost" size="sm">{perPage}</Button>
          </Menu.Trigger>
          <Menu.Content align="start">
            {perPageOptions.map((opt) => (
              <Menu.CheckboxItem
                key={opt}
                checked={opt === perPage}
                onCheckedChange={() => reload(1, opt)}
              >
                {opt}
              </Menu.CheckboxItem>
            ))}
          </Menu.Content>
        </Menu.Root>
      </div>

      <span>
        {total === 0
          ? `No ${itemLabel.plural}`
          : <>Showing <span className="text-fg">{startRow.toLocaleString()}–{endRow.toLocaleString()}</span> of <span className="text-fg">{total.toLocaleString()}</span> {total === 1 ? itemLabel.singular : itemLabel.plural}</>}
      </span>

      <div className="flex items-center gap-1">
        <IconButton
          aria-label="First page"
          size="sm"
          disabled={page <= 1}
          onClick={() => reload(1, perPage)}
        >
          <ChevronsLeft />
        </IconButton>
        <Button
          intent="ghost"
          size="sm"
          disabled={page <= 1}
          onClick={() => reload(page - 1, perPage)}
        >
          <ChevronLeft /> Prev
        </Button>
        <span className="px-1">Page {page} of {totalPages}</span>
        <Button
          intent="ghost"
          size="sm"
          disabled={page >= totalPages}
          onClick={() => reload(page + 1, perPage)}
        >
          Next <ChevronRight />
        </Button>
        <IconButton
          aria-label="Last page"
          size="sm"
          disabled={page >= totalPages}
          onClick={() => reload(totalPages, perPage)}
        >
          <ChevronsRight />
        </IconButton>
      </div>
    </div>
  );
}
